import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Label, Input } from "reactstrap";
import { Controller } from "react-hook-form";
import { getRegions, getCommunes } from "../../api/contracts";
import { useCountry } from "../../context/CountryContext";
import { isValidPostalCode } from "../../components/postalCodeChipsInput";
import { filterRegionsByCoverage, filterCommunesByCoverage } from "../../utils/coverageCommunes";
import Select from "react-select";
import { useTranslation } from "react-i18next";

/**
 * `coverage` / `coverageStatus` llegan del wizard (`resolveCoverage`), que ya
 * los carga para el Excel. Acá solo se usan para achicar los desplegables a lo
 * que cubre el operador; si la cobertura no se puede determinar se muestra el
 * catálogo completo, como antes.
 */
export default function RecipientStep({ methods, coverage = null, coverageStatus = "ready" }) {
  const { control, watch, setValue } = methods;
  const [allRegions, setAllRegions] = useState([]);
  const [allCommunes, setAllCommunes] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingCommunes, setLoadingCommunes] = useState(false);
  // Esperar la cobertura evita mostrar el catálogo entero y achicarlo después,
  // con el usuario ya eligiendo.
  const waitingCoverage = coverageStatus === "loading";

  const { countryId, labels, postalCodeRates, loading: countryLoading } = useCountry();
  const selectedRegion = watch("regionCode");
  // El código postal solo se pide en países donde significa algo para la operación
  // (hoy Argentina, donde además define tarifas de pago al repartidor). En Chile el
  // paso del destinatario queda exactamente como estaba.
  const postalEnabled = postalCodeRates?.enabled === true;
  const { t } = useTranslation();
  const regionWord = labels?.region ?? t("Región");
  const communeWord = labels?.commune ?? t("Comuna");

  // Cargar regiones al montar el componente (con el país del usuario resuelto)
  useEffect(() => {
    if (countryLoading || !countryId) return;
    const fetchRegions = async () => {
      setLoadingRegions(true);
      try {
        const data = await getRegions({ countryId });
        if (!data.error) {
          setAllRegions(data.map(r => ({ value: r.code, label: r.name })));
        }
      } catch (error) {
        console.error("Error loading regions:", error);
      } finally {
        setLoadingRegions(false);
      }
    };
    fetchRegions();
  }, [countryId, countryLoading]);

  // Cargar comunas cuando se selecciona una región
  useEffect(() => {
    if (selectedRegion && countryId) {
      const fetchCommunes = async () => {
        setLoadingCommunes(true);
        try {
          const data = await getCommunes({ countryId, regionId: selectedRegion });
          if (!data.error) {
            setAllCommunes(data.map(c => ({ value: c.name, label: c.name })));
          }
        } catch (error) {
          console.error("Error loading communes:", error);
        } finally {
          setLoadingCommunes(false);
        }
      };
      fetchCommunes();
    } else {
      setAllCommunes([]);
      setValue("municipality", null);
    }
  }, [selectedRegion, setValue, countryId]);

  const regions = useMemo(
    () => filterRegionsByCoverage(allRegions, coverage),
    [allRegions, coverage],
  );
  const communes = useMemo(
    () => filterCommunesByCoverage(allCommunes, selectedRegion, coverage),
    [allCommunes, selectedRegion, coverage],
  );
  const regionsLoading = loadingRegions || waitingCoverage;

  return (
    <div className="recipient-step">
      <Row className="mb-3">
        <Col xs="12" md="6">
          <Label htmlFor="receiver_name" className="recipient-step__label">
            {t("Nombre completo del destinatario *")}
          </Label>
          <Controller
            name="receiver_name"
            control={control}
            rules={{ required: t("El nombre completo es requerido") }}
            render={({ field, fieldState }) => (
              <>
                <Input 
                  {...field} 
                  id="receiver_name"
                  placeholder={t("Nombre completo del destinatario")}
                  invalid={!!fieldState.error} 
                  bsSize="sm"
                />
                {fieldState.error && <small className="text-danger d-block mt-1">{fieldState.error.message}</small>}
              </>
            )}
          />
        </Col>
        <Col xs="12" md="6">
          <Label htmlFor="receiver_phone" className="recipient-step__label">
            {t("Teléfono del destinatario *")}
          </Label>
          <Controller
            name="receiver_phone"
            control={control}
            rules={{ required: t("El teléfono es requerido") }}
            render={({ field, fieldState }) => (
              <>
                <Input 
                  {...field} 
                  id="receiver_phone"
                  placeholder="+56 9 1234 5678" 
                  invalid={!!fieldState.error} 
                  bsSize="sm"
                />
                {fieldState.error && <small className="text-danger d-block mt-1">{fieldState.error.message}</small>}
              </>
            )}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col xs="12" md="6">
          <Label htmlFor="regionCode" className="recipient-step__label">
            {regionWord} de entrega *
          </Label>
          <Controller
            name="regionCode"
            control={control}
            rules={{ required: t("La región es requerida") }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  {...field}
                  inputId="regionCode"
                  options={regions}
                  placeholder={regionsLoading ? t("Cargando...") : t("Selecciona región")}
                  isLoading={regionsLoading}
                  isDisabled={regionsLoading}
                  className="recipient-step__select"
                  classNamePrefix="react-select"
                  onChange={(option) => field.onChange(option?.value)}
                  value={regions.find(r => r.value === field.value) || null}
                />
                {fieldState.error && <small className="text-danger d-block mt-1">{fieldState.error.message}</small>}
              </>
            )}
          />
        </Col>
        <Col xs="12" md="6">
          <Label htmlFor="municipality" className="recipient-step__label">
            {communeWord} de entrega *
          </Label>
          <Controller
            name="municipality"
            control={control}
            rules={{ required: t("La comuna es requerida") }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  {...field}
                  inputId="municipality"
                  options={communes}
                  placeholder={
                    !selectedRegion
                      ? t("Selecciona región primero")
                      : loadingCommunes
                      ? t("Cargando...")
                      : t("Selecciona comuna")
                  }
                  isLoading={loadingCommunes}
                  isDisabled={!selectedRegion || loadingCommunes}
                  className="recipient-step__select"
                  classNamePrefix="react-select"
                  onChange={(option) => field.onChange(option?.value)}
                  value={communes.find(c => c.value === field.value) || null}
                />
                {fieldState.error && <small className="text-danger d-block mt-1">{fieldState.error.message}</small>}
              </>
            )}
          />
        </Col>
      </Row>

      {postalEnabled && (
        <Row className="mb-3">
          <Col xs="12" md="6">
            <Label htmlFor="zip_code" className="recipient-step__label">
              {t("Código postal")}
            </Label>
            <Controller
              name="zip_code"
              control={control}
              rules={{
                validate: (v) =>
                  !v ||
                  String(v).trim() === "" ||
                  isValidPostalCode(v) ||
                  t("Usa un CPA (M5500ABC) o un código de 4 dígitos (5500)"),
              }}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    id="zip_code"
                    value={field.value ?? ""}
                    // El CP se guarda CRUDO (solo trim + uppercase): la derivación
                    // CPA → 4 dígitos la hace el backend al tarifar, no la captura.
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    placeholder={t("Ej: 5500 o M5500ABC")}
                    invalid={!!fieldState.error}
                    bsSize="sm"
                  />
                  {fieldState.error ? (
                    <small className="text-danger d-block mt-1">{fieldState.error.message}</small>
                  ) : (
                    <small className="text-muted d-block mt-1">
                      {t("Opcional. Define la tarifa de entrega cuando el operador la configuró por código postal.")}
                    </small>
                  )}
                </>
              )}
            />
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col xs="12">
          <Label htmlFor="address_line" className="recipient-step__label">
            {t("Dirección de entrega *")}
          </Label>
          <Controller
            name="address_line"
            control={control}
            rules={{ required: t("La dirección es requerida") }}
            render={({ field, fieldState }) => (
              <>
                <Input 
                  {...field} 
                  id="address_line"
                  placeholder={t("Calle y número")}
                  invalid={!!fieldState.error}
                  bsSize="sm"
                />
                {fieldState.error && <small className="text-danger d-block mt-1">{fieldState.error.message}</small>}
                {/* El depto/oficina va en "Comentarios adicionales", no acá. El
                    backend igual lo quita del texto que le manda al
                    geocodificador, pero decirlo evita que el dato quede
                    enterrado dentro de la calle, donde el repartidor no lo ve. */}
                {!fieldState.error && (
                  <small className="text-muted d-block mt-1">
                    {t("Depto, casa u oficina van en Comentarios adicionales.")}
                  </small>
                )}
              </>
            )}
          />
        </Col>
      </Row>

      <Row>
        <Col xs="12">
          <Label htmlFor="comment" className="recipient-step__label">
            {t("Comentarios adicionales")}
          </Label>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <Input 
                {...field} 
                id="comment"
                type="textarea" 
                rows="2" 
                placeholder={t("Instrucciones especiales (opcional)")}
                bsSize="sm"
              />
            )}
          />
        </Col>
      </Row>
    </div>
  );
}

